import React, { useEffect, useState, useRef } from "react";
import {
  Dialog, DialogTitle, DialogContent, TextField, Button,
  FormControlLabel, Switch, Box, Typography, Alert
} from "@mui/material";
import {
  EmailAuthProvider, reauthenticateWithCredential, updatePassword,
  PhoneAuthProvider, PhoneMultiFactorGenerator, multiFactor,
  getMultiFactorResolver, RecaptchaVerifier
} from "firebase/auth";
import { auth } from "../firebase_config";

const Enable2FAModal = ({ open, handleClose }) => {
  const [mfaEnabled, setMfaEnabled] = useState(false); // faktisk status
  const [mfaChecked, setMfaChecked] = useState(false); // användarens val
  const [phone, setPhone] = useState("");
  const [userPassword, setUserPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [message, setMessage] = useState("");

  const recaptchaVerifierRef = useRef(null);

  const getRecaptchaVerifier = async () => {
    // Ta bort gammal instans om den finns
    if (window.recaptchaVerifier && window.recaptchaVerifier.clear) {
      window.recaptchaVerifier.clear();
      window.recaptchaVerifier = null;
    }
  
    // Skapa alltid ny instans
    window.recaptchaVerifier = new RecaptchaVerifier(
      'recaptcha-container',
      { size: 'invisible' },
      auth
    );
  
    await window.recaptchaVerifier.render();
  
    return window.recaptchaVerifier;
  };

  useEffect(() => {
    const checkMFAStatus = async () => {
      const user = auth.currentUser;
      if (!user) return;
      await user.reload();
      console.log(user?.reloadUserInfo);
      const enrolledFactors = user?.reloadUserInfo?.mfaInfo;
      if (enrolledFactors && enrolledFactors.length > 0) {
        setMfaEnabled(true);
        setMfaChecked(true);
        setPhone(enrolledFactors[0]?.phoneNumber || "");
      } else {
        setMfaEnabled(false);
        setMfaChecked(false);
      }
    };
    checkMFAStatus();
  }, [open]);

  const toggleMfa = (event) => {
    setMfaChecked(event.target.checked);
  };

  const normalizePhoneNumber = (input) => {
    let num = input.replace(/\D/g, "");
    if (num.startsWith("0046")) num = num.slice(4);
    else if (num.startsWith("46")) num = num.slice(2);
    if (num.startsWith("0")) num = num.slice(1);
    return `+46${num}`;
  };

  const handleSaveMFA = async () => {
    if (!phone) {
      setMessage("Du måste ange ett telefonnummer.");
      return;
    }

    const formattedPhone = normalizePhoneNumber(phone);
    if (!/^\+467\d{8}$/.test(formattedPhone)) {
      setMessage("Ogiltigt telefonnummer.");
      return;
    }

    const user = auth.currentUser;
    if (!user) return;

    try {
      const credential = EmailAuthProvider.credential(user.email, userPassword);
      await reauthenticateWithCredential(user, credential);

      if (!window.recaptchaVerifier) {
        window.recaptchaVerifier = new RecaptchaVerifier(
          "recaptcha-container",
          { size: "invisible" },
          auth
        );
        await window.recaptchaVerifier.render();
      
        // Spara referens till verifier
        recaptchaVerifierRef.current = window.recaptchaVerifier;
      }

      const session = await multiFactor(user).getSession();
      const phoneProvider = new PhoneAuthProvider(auth);
      const verificationId = await phoneProvider.verifyPhoneNumber(
        { phoneNumber: formattedPhone, session }, window.recaptchaVerifier
      );

      const code = prompt("Ange SMS-koden:");
      if (!code) return;

      const cred = PhoneAuthProvider.credential(verificationId, code);
      const assertion = PhoneMultiFactorGenerator.assertion(cred);
      await multiFactor(user).enroll(assertion, "SMS 2FA");

      setMessage("✅ MFA aktiverad!");
      setMfaEnabled(true);
    } catch (err) {
      if (err.code === "auth/multi-factor-auth-required") {
        setMessage("❗ MFA krävs för att verifiera identitet.");
      } else {
        setMessage(`Fel: ${err.message}`);
      }
    }
  };

  const handlePasswordChange = async () => {
    if (newPassword !== confirmPassword) {
      setMessage("Nya lösenordet matchar inte.");
      return;
    }
  
    try {
      const user = auth.currentUser;
      const credential = EmailAuthProvider.credential(user.email, currentPassword);
  
      try {
        await reauthenticateWithCredential(user, credential);
      } catch (error) {
        if (error.code === 'auth/multi-factor-auth-required') {
          const resolver = getMultiFactorResolver(auth, error);
          const phoneInfo = resolver.hints[0];
  
          // Skapa och hämta ny reCAPTCHA
          const verifier = await getRecaptchaVerifier();
  
          const phoneProvider = new PhoneAuthProvider(auth);
          const verificationId = await phoneProvider.verifyPhoneNumber(
            { multiFactorHint: phoneInfo, session: resolver.session },
            verifier
          );
  
          const verificationCode = prompt('MFA krävs: ange koden du fått via SMS');
          const cred = PhoneAuthProvider.credential(verificationId, verificationCode);
          const multiFactorAssertion = PhoneMultiFactorGenerator.assertion(cred);
          await resolver.resolveSignIn(multiFactorAssertion);
        } else {
          throw error;
        }
      }
  
      await updatePassword(user, newPassword);
      setMessage("✅ Lösenord uppdaterat!");
      handleClose()
    } catch (err) {
      console.error(err);
      setMessage(`❌ Fel: ${err.message}`);
    }
  };

  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        window.recaptchaVerifier = null;
        recaptchaVerifierRef.current = null;
      }
    };
  }, [open]);

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogTitle>Kontoinställningar</DialogTitle>
      <DialogContent sx={{ width: 400 }}>
        {message && <Alert sx={{ mb: 2 }}>{message}</Alert>}

        <FormControlLabel
  control={
    <Switch
      checked={mfaChecked}
      onChange={toggleMfa}
      disabled={mfaEnabled} // Gör slidern inaktiv om MFA redan är aktiverat
    />
  }
  label={mfaEnabled ? "✅ 2FA är aktiverat" : "Aktivera 2FA"}
/>

      {mfaEnabled && (
        <Typography
          variant="body2"
          sx={{ color: 'gray', mt: 1, mb: 2, fontStyle: 'italic' }}
        >
          För att stänga av MFA, kontakta supporten
        </Typography>
      )}

      {mfaChecked && !mfaEnabled && (
        <>
          <TextField
            fullWidth
            variant="outlined"
            label="Telefonnummer"
            placeholder="0701234567"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            sx={{ mt: 2 }}
          />

          <TextField
            type="password"
            fullWidth
            variant="outlined"
            label="Lösenord"
            placeholder="Verifiera lösenord"
            value={userPassword}
            onChange={(e) => setUserPassword(e.target.value)}
            sx={{ mt: 2 }}
          />

          <Button
            variant="contained"
            fullWidth
            color="primary"
            sx={{ mt: 2 }}
            onClick={handleSaveMFA}
          >
            Aktivera MFA
          </Button>
        </>
      )}

<Box sx={{ borderTop: '1px solid #ccc', mt: 4, pt: 2 }}>
  <Typography variant="h6">Byt lösenord</Typography>

  <TextField
    type="password"
    fullWidth
    placeholder="Nuvarande lösenord"
    value={currentPassword}
    onChange={(e) => setCurrentPassword(e.target.value)}
    onKeyDown={(e) => e.key === 'Enter' && handlePasswordChange()}
    sx={{ mt: 1 }}
  />

  <TextField
    type="password"
    fullWidth
    placeholder="Nytt lösenord"
    value={newPassword}
    onChange={(e) => setNewPassword(e.target.value)}
    onKeyDown={(e) => e.key === 'Enter' && handlePasswordChange()}
    sx={{ mt: 1 }}
  />

  <TextField
    type="password"
    fullWidth
    placeholder="Bekräfta nytt lösenord"
    value={confirmPassword}
    onChange={(e) => setConfirmPassword(e.target.value)}
    onKeyDown={(e) => e.key === 'Enter' && handlePasswordChange()}
    sx={{ mt: 1 }}
  />

  <Button
    variant="contained"
    fullWidth
    color="secondary"
    sx={{ mt: 2 }}
    onClick={handlePasswordChange}
  >
    Ändra lösenord
  </Button>
</Box>

        <div id="recaptcha-container" />
      </DialogContent>
    </Dialog>
  );
};

export default Enable2FAModal;